import { useState, useRef, useEffect } from 'react';
import { createRootRoute, createRoute, Outlet, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, Users, FileText, Clock, Settings, Bell, Check, Database, ListTodo, MessageSquare, Menu, X, Sun, Moon, LogOut } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import lettermarkUrl from '@/assets/lettermark.svg';
import wordmarkUrl from '@/assets/wordmark.svg';
import wordmarkLightUrl from '@/assets/wordmark-light.svg';
import { OverviewPage } from '@/pages/OverviewPage';
import { ClientsPage } from '@/pages/ClientsPage';
import { ClientProfilePage } from '@/pages/ClientProfilePage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { DeadlinesPage } from '@/pages/DeadlinesPage';
import { MyDataPage } from '@/pages/MyDataPage';
import { TasksPage } from '@/pages/TasksPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { MessagesPage } from '@/pages/MessagesPage';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, useMessageUnreadCount } from '@/hooks/queries';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { getCurrentToken } from '@/lib/api';
import { useTheme } from '@/hooks/useTheme';

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unread = data?.unreadCount ?? 0;
  const items = data?.data ?? [];

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const typeLabels: Record<string, string> = {
    DOCUMENT_UPLOADED: 'Νέο έγγραφο',
    DEADLINE_APPROACHING: 'Προθεσμία',
    DEADLINE_OVERDUE: 'Εκπρόθεσμη',
    DEADLINE_EXTENDED: 'Παράταση',
    REQUEST_PENDING: 'Αίτημα',
    AI_EXTRACTION_COMPLETE: 'AI Εξαγωγή',
    SYSTEM: 'Σύστημα',
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-full border border-border bg-card hover:border-primary transition-colors"
      >
        <Bell className="h-4 w-4 text-sidebar-muted" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-xs font-bold text-destructive-foreground flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-card rounded-[16px] shadow-lifted-lg border border-border z-50 max-h-96 flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-sm font-semibold text-foreground">Ειδοποιήσεις</span>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                className="text-xs text-primary hover:underline"
              >
                Ανάγνωση όλων
              </button>
            )}
          </div>
          <div className="overflow-auto flex-1">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Καμία ειδοποίηση</p>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer flex items-start gap-2 ${!n.readAt ? 'bg-primary/5' : ''}`}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-muted-foreground">
                        {typeLabels[n.type] ?? n.type}
                      </span>
                      {!n.readAt && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{n.body}</p>
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {new Date(n.createdAt).toLocaleString('el-GR', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {!n.readAt && (
                    <Check className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-1" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Root layout with sidebar
const rootRoute = createRootRoute({
  component: function RootLayout() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: unreadData } = useMessageUnreadCount();
    const msgUnread = unreadData?.unreadCount ?? 0;
    const { theme, toggleTheme } = useTheme();
    const { logout } = useAuth0();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Connect Socket.io and set up event listeners for real-time cache invalidation
    useEffect(() => {
      let mounted = true;

      async function setup() {
        const token = await getCurrentToken();
        if (!mounted) return;
        const socket = connectSocket(token ?? undefined);

        socket.on('document:uploaded', () => {
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });
        socket.on('document:processed', () => {
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });
        socket.on('document:updated', () => {
          queryClient.invalidateQueries({ queryKey: ['documents'] });
        });
        socket.on('deadline:updated', () => {
          queryClient.invalidateQueries({ queryKey: ['deadlines'] });
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });
        socket.on('deadlines:regenerated', () => {
          queryClient.invalidateQueries({ queryKey: ['deadlines'] });
        });
        socket.on('notification', () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });
        socket.on('mydata:synced', () => {
          queryClient.invalidateQueries({ queryKey: ['mydata'] });
        });
        socket.on('task:created', () => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        });
        socket.on('task:updated', () => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        });
        socket.on('task:deleted', () => {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        });
        socket.on('message:new', () => {
          queryClient.invalidateQueries({ queryKey: ['messageThreads'] });
          queryClient.invalidateQueries({ queryKey: ['messageThread'] });
          queryClient.invalidateQueries({ queryKey: ['messageUnreadCount'] });
        });
        socket.on('message:read', () => {
          queryClient.invalidateQueries({ queryKey: ['messageThreads'] });
          queryClient.invalidateQueries({ queryKey: ['messageUnreadCount'] });
        });
      }

      setup();

      return () => {
        mounted = false;
        disconnectSocket();
      };
    }, [queryClient]);

    const navItems = [
      { to: '/' as const, label: t('nav.overview'), icon: LayoutDashboard },
      { to: '/clients' as const, label: t('nav.clients'), icon: Users },
      { to: '/documents' as const, label: t('nav.documents'), icon: FileText },
      { to: '/deadlines' as const, label: t('nav.deadlines'), icon: Clock },
      { to: '/tasks' as const, label: t('nav.tasks'), icon: ListTodo },
      { to: '/messages' as const, label: t('nav.messages'), icon: MessageSquare },
      { to: '/mydata' as const, label: 'myDATA', icon: Database },
      { to: '/settings' as const, label: t('nav.settings'), icon: Settings },
    ];

    const sidebarContent = (
      <>
        <div className="p-4 border-b border-sidebar-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={lettermarkUrl} alt="Taxistry" className="w-8 h-8 rounded-lg shadow-sm" />
              <img src={theme === 'dark' ? wordmarkLightUrl : wordmarkUrl} alt="Taxistry" className="h-4" />
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-full border border-border bg-card hover:border-primary transition-all"
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4 text-sidebar-muted" />
                ) : (
                  <Moon className="h-4 w-4 text-sidebar-muted" />
                )}
              </button>
              <NotificationBell />
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.to === '/' }}
              className="nav-active-bar flex items-center gap-3 px-3 py-2 rounded-[10px] text-sm text-sidebar-muted hover:text-foreground hover:bg-sidebar-active transition-all [&.active]:bg-brand-soft [&.active]:text-brand-strong [&.active]:font-bold"
              onClick={() => setMobileMenuOpen(false)}
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {item.to === '/messages' && msgUnread > 0 ? (
                <span className="h-4 min-w-4 px-1 rounded-full bg-primary text-xs font-bold text-primary-foreground flex items-center justify-center">
                  {msgUnread > 99 ? '99+' : msgUnread}
                </span>
              ) : null}
            </Link>
          ))}
        </nav>

        <div className="p-3 border-t border-sidebar-border">
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="flex items-center gap-3 px-3 py-2 rounded-[10px] text-sm text-sidebar-muted hover:text-destructive hover:bg-sidebar-active transition-all w-full"
          >
            <LogOut className="h-4 w-4" />
            <span>Αποσύνδεση</span>
          </button>
        </div>
      </>
    );

    return (
      <div className="flex h-screen bg-background">
        {/* Mobile hamburger */}
        <button
          className="fixed top-3 left-3 z-50 p-2 rounded-[10px] bg-card border border-border shadow-sm md:hidden"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar — desktop: static, mobile: slides from left */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-50 w-56 bg-sidebar/95 backdrop-blur-xl border-r border-sidebar-border flex flex-col flex-shrink-0
            transition-transform duration-200 ease-in-out
            md:static md:translate-x-0
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Mobile close button */}
          <button
            className="absolute top-3 right-3 p-1 rounded-md hover:bg-sidebar-active md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="h-4 w-4 text-sidebar-muted" />
          </button>
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    );
  },
});

// Index route (Overview)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: OverviewPage,
});

// Clients route
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  component: ClientsPage,
});

// Client profile route
const clientProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$clientId',
  component: function ClientProfile() {
    const { clientId } = clientProfileRoute.useParams();
    return <ClientProfilePage clientId={clientId} />;
  },
});

// Documents route
const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  component: DocumentsPage,
});

// Deadlines route
const deadlinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deadlines',
  component: DeadlinesPage,
});

// Tasks route
const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  component: TasksPage,
});

// Messages route
const messagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/messages',
  component: MessagesPage,
});

// myDATA route
const mydataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mydata',
  component: MyDataPage,
});

// Settings route
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});

// Build route tree
export const routeTree = rootRoute.addChildren([
  indexRoute,
  clientsRoute,
  clientProfileRoute,
  documentsRoute,
  deadlinesRoute,
  tasksRoute,
  messagesRoute,
  mydataRoute,
  settingsRoute,
]);
